import React, { useEffect, useId, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { BasicButton, ButtonType, Dropdown, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, MoreActions } from "@gaming/xds-react-js-icons";

import { Modal } from "../../components/Modal/Modal";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import {
  getPartnerBillingAccountPath,
  getPartnerShippingAddressPath,
  partnerAccessDeniedErrors,
  partnerAccessDeniedPath,
  partnerQueryParameters,
  partnerSectionLinks,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import {
  BillingAccount,
  DevelopmentHardwareStoreStatus,
  DeviceOrderingAddress,
  DeviceOrderingAddressesResponse,
} from "../../models/developmentHardware";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import * as styles from "./PartnerDeviceOrderingAddressesPage.module.css";

const addressesUrl = "/games/partner/development-hardware-addresses";
const getShippingAddressUrl = (billingAccountId: string, shippingAddressId: string) =>
  `/games/partner/development-hardware-billing-accounts/${billingAccountId}/shipping-addresses/${shippingAddressId}`;

const AddressDetails = ({
  address,
  includePhone = false,
}: Readonly<{ address: DeviceOrderingAddress; includePhone?: boolean }>) => (
  <address className={styles.addressDetails}>
    <span>{address.line1}</span>
    {address.line2 && <span>{address.line2}</span>}
    <span>
      {address.city}, {address.stateOrProvince} {address.postalCode}
    </span>
    <span>{address.countryName}</span>
    {includePhone && address.phone && <span className={styles.phone}>{address.phone}</span>}
  </address>
);

type ShippingAddressActionMenuProps = Readonly<{
  editLabel: string;
  moreOptionsAriaLabel: string;
  removeLabel: string;
  onEdit: () => void;
  onRemove: () => void;
}>;

const ShippingAddressActionMenu = ({
  editLabel,
  moreOptionsAriaLabel,
  removeLabel,
  onEdit,
  onRemove,
}: ShippingAddressActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectAction = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  return (
    <div className={styles.actionMenu}>
      <button
        ref={triggerRef}
        className={styles.actionMenuButton}
        type="button"
        aria-label={moreOptionsAriaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        <Icon aria-hidden="true" icon={MoreActions} iconSize={IconSize.XSmall} />
      </button>
      {isOpen && (
        <Dropdown
          isOpen
          onClose={() => setIsOpen(false)}
          relativeRef={triggerRef as React.RefObject<HTMLElement>}
          horizontalAlignment="right"
          backgroundDismissable
          dimBackground="none"
          containerClassName={styles.actionMenuContainer}
          className={styles.actionMenuPopup}
          zIndex={1000}
        >
          <div role="menu">
            <button
              className={styles.actionMenuItem}
              role="menuitem"
              type="button"
              onClick={() => selectAction(onEdit)}
            >
              {editLabel}
            </button>
            <button
              className={styles.actionMenuItem}
              role="menuitem"
              type="button"
              onClick={() => selectAction(onRemove)}
            >
              {removeLabel}
            </button>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

type RemoveShippingAddressModalProps = Readonly<{
  address: DeviceOrderingAddress | null;
  cancelLabel: string;
  closeAriaLabel: string;
  confirmLabel: string;
  description: string;
  error: string;
  isBusy: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}>;

const RemoveShippingAddressModal = ({
  address,
  cancelLabel,
  closeAriaLabel,
  confirmLabel,
  description,
  error,
  isBusy,
  title,
  onCancel,
  onConfirm,
}: RemoveShippingAddressModalProps) => {
  const titleId = useId();

  return (
    <Modal
      isOpen={address !== null}
      onClose={isBusy ? () => undefined : onCancel}
      ariaLabelledBy={titleId}
    >
      {address && (
        <div className={styles.removeModal} aria-busy={isBusy}>
          <div className={styles.removeModalHeader}>
            <h2 id={titleId}>{title}</h2>
            <button
              type="button"
              className={styles.removeModalClose}
              aria-label={closeAriaLabel}
              disabled={isBusy}
              onClick={onCancel}
            >
              <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
            </button>
          </div>
          <p className={styles.removeModalDescription}>{description}</p>
          <div className={styles.removeAddressCard}>
            <strong>{address.name}</strong>
            <AddressDetails address={address} />
          </div>
          {error && (
            <p className={styles.removeError} role="alert">
              {error}
            </p>
          )}
          <div className={styles.removeModalActions}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              type="button"
              disabled={isBusy}
              onClick={onCancel}
            >
              {cancelLabel}
            </BasicButton>
            <BasicButton
              as="button"
              buttonType={ButtonType.Brand}
              className={styles.destructiveButton}
              type="button"
              disabled={isBusy}
              onClick={onConfirm}
            >
              {confirmLabel}
            </BasicButton>
          </div>
        </div>
      )}
    </Modal>
  );
};

type SelectedShippingAddress = Readonly<{
  billingAccountId: string;
  address: DeviceOrderingAddress;
}>;

export const PartnerDeviceOrderingAddressesPage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSigned);
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const data = useSelector(getXboxOnboardData);
  const history = useHistory();
  const hasPartnerData = data !== null;
  const isHubAdmin = data?.isHubAdmin === true;
  const [
    pageLabel,
    overviewLabel,
    description,
    addBillingAccountLabel,
    billingAccountNameLabel,
    shippingAddressesLabel,
    financialContactLabel,
    regionLabel,
    countryLabel,
    statusLabel,
    openStatus,
    closedStatus,
    loadingLabel,
    emptyLabel,
    errorLabel,
    underReviewLabel,
    pendingDescription,
    pendingTimeline,
    submittedLabel,
    viewSubmissionLabel,
    addShippingAddressLabel,
    editBillingAccountLabel,
    viewDetailsLabel,
    moreOptionsAriaLabel,
    editLabel,
    removeAddressLabel,
    removeAddressTitle,
    removeAddressDescription,
    closeAriaLabel,
    cancelLabel,
    removeLabel,
    removeAddressError,
  ] = useStrings(
    "deviceOrderingAddressesLabel",
    "partnerOverviewPageTitle",
    "developmentHardwareAddressesDescription",
    "developmentHardwareAddBillingAccount",
    "developmentHardwareBillingAccountLabel",
    "developmentHardwareShippingAddresses",
    "developmentHardwareFinancialContact",
    "developmentHardwareRegionLabel",
    "countryOrRegion",
    "status",
    "developmentHardwareStoreStatusOpen",
    "developmentHardwareStoreStatusClosed",
    "developmentHardwareAddressesLoading",
    "developmentHardwareAddressesEmpty",
    "developmentHardwareAddressesError",
    "underReview",
    "developmentHardwarePendingDescription",
    "developmentHardwarePendingTimeline",
    "submitted",
    "developmentHardwareViewSubmission",
    "developmentHardwareAddShippingAddress",
    "developmentHardwareEditBillingAccount",
    "viewDetails",
    "moreOptionsAriaLabel",
    "edit",
    "developmentHardwareRemoveShippingAddressLabel",
    "developmentHardwareRemoveShippingAddressTitle",
    "developmentHardwareRemoveShippingAddressDescription",
    "closeAriaLabel",
    "cancelButton",
    "ticketTeamDetailRemove",
    "developmentHardwareRemoveShippingAddressError"
  );
  const [apacRegion, northAmericaRegion, emeaRegion, japanRegion, latamRegion, chinaRegion] =
    useStrings(
      "developmentHardwareRegionApac",
      "northAmericanTerritory",
      "developmentHardwareRegionEmea",
      "japanTerritory",
      "developmentHardwareRegionLatam",
      "china"
    );
  const regionLabels = new Map([
    ["apac", apacRegion],
    ["northAmerica", northAmericaRegion],
    ["emea", emeaRegion],
    ["japan", japanRegion],
    ["latam", latamRegion],
    ["china", chinaRegion],
  ]);
  const [billingAccounts, setBillingAccounts] = useState<ReadonlyArray<BillingAccount>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<SelectedShippingAddress | null>(null);
  const [isRemovingAddress, setIsRemovingAddress] = useState(false);
  const [removeError, setRemoveError] = useState("");

  useEffect(() => {
    if (isPageReady && hasPartnerData && !isHubAdmin) {
      history.replace(
        `${partnerAccessDeniedPath}?${partnerQueryParameters.accessDeniedError}=${partnerAccessDeniedErrors.admin}`
      );
    }
  }, [hasPartnerData, history, isHubAdmin, isPageReady]);

  useEffect(() => {
    if (!isPageReady || !isSignedIn || !hasPartnerData || !isHubAdmin) {
      return;
    }

    let active = true;
    setIsLoading(true);
    setLoadFailed(false);
    fetch(addressesUrl)
      .then(async response => {
        if (response.status === 403) {
          if (active) {
            dispatch(xboxOnboardActions.updateDevelopmentHardwareAccess(false));
            setIsLoading(false);
            history.replace(
              `${partnerAccessDeniedPath}?${partnerQueryParameters.accessDeniedError}=${partnerAccessDeniedErrors.admin}`
            );
          }
          return null;
        }
        if (!response.ok) {
          throw new Error(`Failed to load device ordering addresses: ${response.status}`);
        }
        return (await response.json()) as DeviceOrderingAddressesResponse;
      })
      .then(response => {
        if (active && response) {
          dispatch(xboxOnboardActions.updateDevelopmentHardwareAccess(true));
          setBillingAccounts(response.billingAccounts);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true);
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [dispatch, hasPartnerData, history, isHubAdmin, isPageReady, isSignedIn]);

  const getStatus = (status: DevelopmentHardwareStoreStatus) =>
    status === DevelopmentHardwareStoreStatus.Open
      ? openStatus
      : status === DevelopmentHardwareStoreStatus.Closed
      ? closedStatus
      : underReviewLabel;
  const getStatusClass = (status: DevelopmentHardwareStoreStatus) =>
    status === DevelopmentHardwareStoreStatus.Open
      ? styles.statusOpen
      : status === DevelopmentHardwareStoreStatus.Closed
      ? styles.statusClosed
      : styles.statusPending;

  const closeRemoveModal = () => {
    if (!isRemovingAddress) {
      setSelectedAddress(null);
      setRemoveError("");
    }
  };

  const removeShippingAddress = async () => {
    if (!selectedAddress) {
      return;
    }

    setIsRemovingAddress(true);
    setRemoveError("");
    try {
      const response = await fetch(
        getShippingAddressUrl(selectedAddress.billingAccountId, selectedAddress.address.addressId),
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error(`Failed to remove shipping address: ${response.status}`);
      }
      setBillingAccounts(current =>
        current.map(account =>
          account.billingAccountId === selectedAddress.billingAccountId
            ? {
                ...account,
                shippingAddresses: account.shippingAddresses.filter(
                  address => address.addressId !== selectedAddress.address.addressId
                ),
              }
            : account
        )
      );
      setSelectedAddress(null);
    } catch {
      setRemoveError(removeAddressError);
    } finally {
      setIsRemovingAddress(false);
    }
  };

  if (!isPageReady || !isSignedIn || !data) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={pageLabel}
        breadcrumbSteps={[{ label: overviewLabel, to: partnerSectionLinks.overview.to }]}
      >
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }
  if (!isHubAdmin) {
    return null;
  }

  return (
    <PartnerPageLayout
      breadcrumbLabel={pageLabel}
      breadcrumbSteps={[{ label: overviewLabel, to: partnerSectionLinks.overview.to }]}
    >
      <ScreenReaderHeading>{pageLabel}</ScreenReaderHeading>
      <header className={styles.header}>
        <div>
          <h1>{pageLabel}</h1>
          <p>{description}</p>
        </div>
        <BasicButton
          as="button"
          buttonType={ButtonType.Brand}
          type="button"
          onClick={() => history.push(getPartnerBillingAccountPath(uuidv4()))}
        >
          {addBillingAccountLabel}
        </BasicButton>
      </header>

      {isLoading ? (
        <div role="status" aria-live="polite" aria-label={loadingLabel} aria-busy="true">
          <PageLoadingSpinner />
        </div>
      ) : loadFailed ? (
        <p role="alert">{errorLabel}</p>
      ) : billingAccounts.length === 0 ? (
        <p>{emptyLabel}</p>
      ) : (
        <div className={styles.billingAccounts}>
          {billingAccounts.map(account => (
            <article className={styles.billingAccount} key={account.billingAccountId}>
              <div className={styles.accountOverview}>
                <header className={styles.accountHeader}>
                  <div className={styles.accountIdentity}>
                    <span className={styles.accountType}>{billingAccountNameLabel}</span>
                    <h2>{account.name}</h2>
                    <AddressDetails address={account.mainAddress} />
                  </div>
                  <div
                    className={styles.accountStatus}
                    aria-label={`${statusLabel}: ${getStatus(account.storeStatus)}`}
                  >
                    <span
                      className={`${styles.statusDot} ${getStatusClass(account.storeStatus)}`}
                      aria-hidden="true"
                    />
                    <span>{getStatus(account.storeStatus)}</span>
                  </div>
                </header>

                {account.storeStatus === DevelopmentHardwareStoreStatus.Pending ? (
                  <section className={styles.pendingReview}>
                    <p>{pendingDescription}</p>
                    <p className={styles.pendingTimeline}>
                      {pendingTimeline}
                      {account.submittedDate && (
                        <span>
                          {submittedLabel}: {new Date(account.submittedDate).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                    <BasicButton
                      as="button"
                      buttonType={ButtonType.Secondary}
                      type="button"
                      onClick={() =>
                        history.push(getPartnerBillingAccountPath(account.billingAccountId))
                      }
                    >
                      {viewSubmissionLabel}
                    </BasicButton>
                  </section>
                ) : (
                  <dl className={styles.metadata}>
                    <div>
                      <dt>{financialContactLabel}</dt>
                      <dd>
                        {account.financialContactName}
                        {account.financialContactEmail && (
                          <span>{account.financialContactEmail}</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>{regionLabel}</dt>
                      <dd>{regionLabels.get(account.region) ?? account.region}</dd>
                    </div>
                    <div>
                      <dt>{countryLabel}</dt>
                      <dd>{account.countryName}</dd>
                    </div>
                  </dl>
                )}
              </div>

              {account.storeStatus !== DevelopmentHardwareStoreStatus.Pending && (
                <>
                  <section className={styles.shippingSection}>
                    <h3>
                      {shippingAddressesLabel} ({account.shippingAddresses.length})
                    </h3>
                    <div className={styles.shippingAddresses}>
                      {account.shippingAddresses.map(address => (
                        <article className={styles.shippingAddress} key={address.addressId}>
                          <div className={styles.shippingAddressHeader}>
                            <h4>{address.name}</h4>
                            {account.storeStatus === DevelopmentHardwareStoreStatus.Open &&
                              address.isPrimaryBillingAddress !== true &&
                              address.isActive !== false && (
                                <ShippingAddressActionMenu
                                  editLabel={editLabel}
                                  moreOptionsAriaLabel={moreOptionsAriaLabel}
                                  removeLabel={removeAddressLabel}
                                  onEdit={() =>
                                    history.push(
                                      getPartnerShippingAddressPath(
                                        account.billingAccountId,
                                        address.addressId
                                      )
                                    )
                                  }
                                  onRemove={() => {
                                    setRemoveError("");
                                    setSelectedAddress({
                                      billingAccountId: account.billingAccountId,
                                      address,
                                    });
                                  }}
                                />
                              )}
                          </div>
                          <AddressDetails address={address} includePhone />
                        </article>
                      ))}
                    </div>
                  </section>
                  <div className={styles.cardActions}>
                    {account.storeStatus === DevelopmentHardwareStoreStatus.Open ? (
                      <>
                        <BasicButton
                          as="button"
                          buttonType={ButtonType.Brand}
                          type="button"
                          onClick={() =>
                            history.push(
                              getPartnerShippingAddressPath(account.billingAccountId, uuidv4())
                            )
                          }
                        >
                          {addShippingAddressLabel}
                        </BasicButton>
                        <BasicButton
                          as="button"
                          buttonType={ButtonType.Secondary}
                          type="button"
                          onClick={() =>
                            history.push(getPartnerBillingAccountPath(account.billingAccountId))
                          }
                        >
                          {editBillingAccountLabel}
                        </BasicButton>
                      </>
                    ) : (
                      <BasicButton
                        as="button"
                        buttonType={ButtonType.Secondary}
                        type="button"
                        onClick={() =>
                          history.push(getPartnerBillingAccountPath(account.billingAccountId))
                        }
                      >
                        {viewDetailsLabel}
                      </BasicButton>
                    )}
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      )}
      <RemoveShippingAddressModal
        address={selectedAddress?.address ?? null}
        cancelLabel={cancelLabel}
        closeAriaLabel={closeAriaLabel}
        confirmLabel={removeLabel}
        description={removeAddressDescription}
        error={removeError}
        isBusy={isRemovingAddress}
        title={removeAddressTitle}
        onCancel={closeRemoveModal}
        onConfirm={removeShippingAddress}
      />
    </PartnerPageLayout>
  );
};
